// US-ALLOWANCE-01 -- Guardian manages recurring allowance schedules.

import {
  type AccountListEntry,
  type AllowanceSchedule,
  accountsList,
  allowanceCancel,
  allowanceCreate,
  allowanceList,
  allowancePause,
} from '@chorepop/core/client';
import {
  type Component,
  For,
  Show,
  createEffect,
  createMemo,
  createResource,
  createSignal,
} from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FREQ_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

type Props = {
  sessionId: string;
  currency: string;
  initialChildAccountId?: number | undefined;
  childDisplayName?: string | undefined;
  onBack: () => void;
};

export const AllowanceScreen: Component<Props> = (props) => {
  const [children] = createResource(async () => {
    const r = await accountsList();
    return r.ok ? r.value.filter((a) => a.roleCode === 'child') : ([] as AccountListEntry[]);
  });

  const [schedules, { refetch }] = createResource(
    () => props.sessionId,
    (sid) => allowanceList(sid).then((r) => (r.ok ? r.value : [])),
  );

  const [showForm, setShowForm] = createSignal(false);
  const [childId, setChildId] = createSignal<number | null>(props.initialChildAccountId ?? null);
  const [frequency, setFrequency] = createSignal<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [amount, setAmount] = createSignal('');
  const [dayOfWeek, setDayOfWeek] = createSignal(1);
  const [dayOfMonth, setDayOfMonth] = createSignal(1);
  const [startDate, setStartDate] = createSignal(new Date().toISOString().slice(0, 10));
  const [label, setLabel] = createSignal('Allowance');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [pausingId, setPausingId] = createSignal<number | null>(null);
  const [pauseUntil, setPauseUntil] = createSignal('');
  const visibleSchedules = createMemo(() => {
    const all = schedules() ?? [];
    return props.initialChildAccountId
      ? all.filter((schedule) => schedule.childAccountId === props.initialChildAccountId)
      : all;
  });

  createEffect(() => {
    if (props.initialChildAccountId && childId() === null) {
      setChildId(props.initialChildAccountId);
    }
  });

  const formatMajor = (minor: number) => `${props.currency} ${(minor / 100).toFixed(2)}`;

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    const cid = childId();
    if (!cid) {
      setError('Choose a child.');
      return;
    }
    const minor = Math.round(Number.parseFloat(amount() || '0') * 100);
    if (minor <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await allowanceCreate({
      sessionId: props.sessionId,
      childAccountId: cid,
      frequency: frequency(),
      amountMinorUnits: minor,
      dayOfWeek: ['weekly', 'biweekly'].includes(frequency()) ? dayOfWeek() : null,
      dayOfMonth: frequency() === 'monthly' ? dayOfMonth() : null,
      startDate: startDate(),
      label: label() || 'Allowance',
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setShowForm(false);
    setAmount('');
    setChildId(props.initialChildAccountId ?? null);
    void refetch();
  };

  const handlePause = async (s: AllowanceSchedule) => {
    setBusy(true);
    await allowancePause(props.sessionId, s.scheduleId, pauseUntil() || null);
    setBusy(false);
    setPausingId(null);
    void refetch();
  };

  const handleCancel = async (s: AllowanceSchedule) => {
    if (!confirm(`Stop allowance for ${s.childDisplayName}?`)) return;
    setBusy(true);
    await allowanceCancel(props.sessionId, s.scheduleId);
    setBusy(false);
    void refetch();
  };

  return (
    <ActionScreen
      eyebrow="Regular money, set your way"
      title={props.childDisplayName ? `${props.childDisplayName}'s allowance` : 'Allowance'}
      onBack={props.onBack}
      footer={
        showForm() ? (
          <button
            type="submit"
            form="allowance-form"
            class="primary action-screen-primary"
            disabled={busy()}
          >
            {busy() ? 'Saving…' : 'Save schedule'}
          </button>
        ) : (
          <button
            type="button"
            class="primary action-screen-primary"
            onClick={() => setShowForm(true)}
            data-testid="add-allowance-btn"
          >
            Set up allowance
          </button>
        )
      }
    >
      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>

      <Show when={showForm()}>
        <form id="allowance-form" onSubmit={handleCreate} class="action-form allowance-form">
          <h2>New allowance schedule</h2>
          <Show when={!props.initialChildAccountId}>
            <div class="field-row">
              <label for="allowance-child">Child</label>
              <select
                id="allowance-child"
                value={childId() ?? ''}
                onChange={(e) => setChildId(Number(e.currentTarget.value) || null)}
              >
                <option value="">-- select child --</option>
                <For each={children() ?? []}>
                  {(c: AccountListEntry) => <option value={c.accountId}>{c.displayName}</option>}
                </For>
              </select>
            </div>
          </Show>
          <div class="field-row">
            <label for="allowance-frequency">Frequency</label>
            <select
              id="allowance-frequency"
              value={frequency()}
              onChange={(e) =>
                setFrequency(e.currentTarget.value as 'weekly' | 'biweekly' | 'monthly')
              }
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every two weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <Show when={frequency() !== 'monthly'}>
            <div class="field-row">
              <span>Pay on</span>
              <div class="day-picker">
                <For each={DAY_LABELS}>
                  {(lbl, i) => (
                    <button
                      type="button"
                      class={dayOfWeek() === i() ? 'day-btn day-btn--on' : 'day-btn'}
                      onClick={() => setDayOfWeek(i())}
                    >
                      {lbl}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </Show>
          <Show when={frequency() === 'monthly'}>
            <div class="field-row">
              <label for="allowance-dom">Day of month (1-28)</label>
              <input
                id="allowance-dom"
                type="number"
                min="1"
                max="28"
                value={dayOfMonth()}
                onInput={(e) => setDayOfMonth(Number(e.currentTarget.value))}
              />
            </div>
          </Show>
          <div class="field-row">
            <label for="allowance-amount">Amount ({props.currency})</label>
            <input
              id="allowance-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount()}
              onInput={(e) => setAmount(e.currentTarget.value)}
            />
          </div>
          <div class="field-row">
            <label for="allowance-start">Start date</label>
            <input
              id="allowance-start"
              type="date"
              value={startDate()}
              onInput={(e) => setStartDate(e.currentTarget.value)}
            />
          </div>
          <div class="field-row">
            <label for="allowance-label">Label in history</label>
            <input
              id="allowance-label"
              type="text"
              value={label()}
              onInput={(e) => setLabel(e.currentTarget.value)}
            />
          </div>
          <button type="button" class="ghost" onClick={() => setShowForm(false)}>
            Cancel setup
          </button>
        </form>
      </Show>

      <Show when={!schedules.loading && visibleSchedules().length === 0 && !showForm()}>
        <p class="settings-section-intro" style="color: var(--ink-2);">
          No allowance schedules yet.
        </p>
      </Show>

      <ul class="routine-list action-allowance-list">
        <For each={visibleSchedules()}>
          {(s) => (
            <li class="routine-row">
              <div class="routine-row-header">
                <span class="routine-row-name">
                  {s.childDisplayName} &mdash; {formatMajor(s.amountMinorUnits)}
                </span>
                <Show when={s.pausedUntilDate}>
                  <span class="routine-badge routine-badge--paused">
                    Paused until {s.pausedUntilDate}
                  </span>
                </Show>
              </div>
              <div class="routine-row-meta">
                <span>{FREQ_LABELS[s.frequency]}</span>
                <Show when={s.dayOfWeek != null}>
                  <span>&bull; {DAY_LABELS[s.dayOfWeek ?? -1] ?? ''}</span>
                </Show>
                <Show when={s.dayOfMonth != null}>
                  <span>&bull; day {s.dayOfMonth}</span>
                </Show>
                <span>&bull; {s.label}</span>
              </div>
              <Show when={pausingId() === s.scheduleId}>
                <div class="routine-pause-panel">
                  <label for={`allowance-pause-${s.scheduleId}`}>
                    Pause until (blank = indefinite):
                  </label>
                  <input
                    id={`allowance-pause-${s.scheduleId}`}
                    type="date"
                    value={pauseUntil()}
                    onInput={(e) => setPauseUntil(e.currentTarget.value)}
                  />
                  <div class="routine-pause-actions">
                    <button
                      type="button"
                      class="primary"
                      disabled={busy()}
                      onClick={() => handlePause(s)}
                    >
                      Confirm
                    </button>
                    <button type="button" class="ghost" onClick={() => setPausingId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </Show>
              <div class="routine-row-actions">
                <Show when={!s.pausedUntilDate}>
                  <button
                    type="button"
                    class="ghost"
                    disabled={busy()}
                    onClick={() => {
                      setPausingId(s.scheduleId);
                      setPauseUntil('');
                    }}
                  >
                    Pause
                  </button>
                </Show>
                <Show when={s.pausedUntilDate}>
                  <button
                    type="button"
                    class="ghost"
                    disabled={busy()}
                    onClick={async () => {
                      setBusy(true);
                      await allowancePause(props.sessionId, s.scheduleId, null);
                      setBusy(false);
                      void refetch();
                    }}
                  >
                    Resume
                  </button>
                </Show>
                <button
                  type="button"
                  class="ghost"
                  style="color: var(--color-danger);"
                  disabled={busy()}
                  onClick={() => handleCancel(s)}
                >
                  Stop
                </button>
              </div>
            </li>
          )}
        </For>
      </ul>
    </ActionScreen>
  );
};
